import React, { useState } from "react";

import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import { Autocomplete, Stack, TextField } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { NumberInput } from "components/forms";
import dayjs from "dayjs";
import { useDateFormatSelect } from "store";
import { CurrencyType } from "store/types";
import toIsoMidday from "utils/to-iso-midday";

import { currenciesArray } from "../../../../constants";
import { State } from "./EditTableCell";

interface Props {
  columnType: "measure" | "characteristic";
  isAbsolute: boolean;
  isNegative: boolean;
  isCurrency: boolean;
  currency?: CurrencyType;
  container?: HTMLElement | null;

  state: State["newMeasureData"];
  setState: React.Dispatch<React.SetStateAction<State["newMeasureData"]>>;
}

export const AddMeasureDataForm: React.FC<Props> = ({
  columnType,
  isAbsolute,
  isNegative,
  isCurrency,
  currency,
  container,

  state,
  setState
}) => {
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [allowNegative, setAllowNegative] = useState<boolean>(true);
  const [edited, setEdited] = useState<boolean>(false);

  const { date, value, selectedCurrency, description } = state;

  const dateFormat = useDateFormatSelect();

  const showCurrencyInput = columnType === "measure" && isCurrency;

  return (
    <Stack sx={{ gap: "10px" }}>
      <Stack sx={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
        {columnType === "measure" && (
          <DesktopDatePicker
            slots={{ openPickerIcon: TodayOutlinedIcon }}
            views={["year", "month", "day"]}
            open={openDatePicker}
            onClose={() => setOpenDatePicker(false)}
            format={dateFormat || "DD/MM/YY"}
            value={dayjs(date)}
            slotProps={{
              inputAdornment: { position: "start" },
              openPickerButton: { onClick: () => setOpenDatePicker(true) },
              popper: {
                container: container ?? undefined,
                sx: {
                  minWidth: "335px",
                  ".MuiPickersCalendarHeader-labelContainer": {
                    fontSize: "0.9rem"
                  }
                }
              },
              textField: {
                onClick: () => setOpenDatePicker(true),
                // onKeyDown: (e) => e.preventDefault(),
                error: false,
                sx: {
                  "& .MuiOutlinedInput-input": {
                    padding: 0,
                    fontSize: "15px",
                    width: "112px",
                    height: "30px",
                    textAlign: "center"
                  },
                  "& .MuiOutlinedInput-root": {
                    // paddingLeft: "20px"
                  },
                  "& .MuiIconButton-root": { padding: 0 },
                  "& .MuiSvgIcon-root": { width: "15px", color: "#5d7782" },
                  "& .MuiInputAdornment-root": { width: "0px" }
                }
              }
            }}
            onChange={(value) => {
              value && value.isValid() && setState((prev) => ({ ...prev, date: toIsoMidday(value?.toISOString()) }));
            }}
          />
        )}
        <NumberInput
          placeholder="Value"
          onFocus={(e) => {
            if (!e) return;
            if (isAbsolute) {
              //if value absolute it means it can't contain minus sign => so prop allowNegative should be changed to false
              setAllowNegative(false);
            } else {
              // the opposite thing, if value not absolute, in can contain minus, so it can be any value (user enter what he want)
              setAllowNegative(true);
            }
          }}
          inputProps={{ maxLength: 11, allowNegative: allowNegative }} // depend from measure is_absolute: boolean flag
          sx={{
            "& .MuiOutlinedInput-input": { textAlign: "center", height: "30px", width: "150px", padding: "0" }
          }}
          value={value}
          onBlur={(e) => {
            if (isNegative) {
              //we need handling case if measure is_negative: true in moment when user click to input
              const pureValue = e.target.value.replace(/,/g, "");
              const value = isAbsolute
                ? Math.abs(Number(pureValue))
                : edited //this value can be changed to opposite only one time, to prevent user from repeating of reversing value we added flag to hint us that value inside input was edited, so if it was edited we can't multiply by "-1" it again!!!
                  ? Number(pureValue)
                  : Number(pureValue) * -1;
              // if measure is_absolute: true, we need to take Math.abs from this value, it means that value can be positive only
              // if measure is_absolute: false && is_negative: true, we need multiply the value by -1, so if value was positive after multiplying it should be negative (and opposite case)
              setState((prev) => ({ ...prev, value: String(value) }));
              setEdited(true);
            } else {
              setState((prev) => ({ ...prev, value: e.target.value.replace(/,/g, "") })); //if is_negative: false just do nothing specific, user enter what he want without any manipulations
            }
          }}
        />
        {showCurrencyInput && (
          <Autocomplete
            options={currenciesArray}
            value={selectedCurrency}
            defaultValue={currency}
            onChange={(_, option) => setState((prev) => ({ ...prev, selectedCurrency: option }))}
            disableClearable
            slotProps={{ popper: { container: container ?? undefined } }}
            // PaperComponent={({ children }) => (
            //   <Paper style={{ display: "flex", flexDirection: "column", alignItems: "center", overflow: "hidden" }}>
            //     {children}
            //   </Paper>
            // )}
            renderInput={(params) => (
              <TextField
                sx={{
                  width: "62px",
                  height: "30px",
                  padding: 0,
                  "& .MuiOutlinedInput-root": {
                    fontSize: "15px",
                    padding: "0 !important"
                  },
                  "& .MuiAutocomplete-endAdornment": {
                    right: "0 !important"
                    // top: "auto"
                  },
                  "& .MuiAutocomplete-popupIndicator:hover": {
                    backgroundColor: "transparent"
                  }
                }}
                {...params}
              />
            )}
          />
        )}
      </Stack>
      <TextField
        value={description}
        placeholder="Add Comment"
        onChange={(e) => setState((prev) => ({ ...prev, description: e.target.value }))}
        sx={{
          "& .MuiOutlinedInput-input": { height: "30px", width: "100%", padding: "0 10px" }
        }}
      />
    </Stack>
  );
};
